<script setup>
import { useMainStore } from '@/stores/main'
import { onMounted, ref } from 'vue'
import CreateForm from '../../../components/SlideOver/forms/CreateApiKey.vue'
import User from '../../../api/User'
import ContentBlock from '@/components/common/ContentBlock.vue'

const mainStore = useMainStore()
const keys = ref([])

onMounted(async () => {
  keys.value = (await User.list_api_keys()).data
})

function show_create_key() {
  mainStore.show_slideover(CreateForm, {})
}

async function click_handler(id) {
  await User.delete_api_key(id)
  keys.value = (await User.list_api_keys()).data
}

mainStore.event_on('created_apikey', async () => {
  keys.value = (await User.list_api_keys()).data
})
</script>

<template>
  <div class="apikeys-content">
    <!-- Empty State -->
    <div v-if="keys.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'key']" class="empty-icon" />
      <p class="empty-title">No API Keys</p>
      <p class="empty-description">You don't have any active API keys yet. Create one to get started.</p>

      <!-- Add API Key Button (inside empty state) -->
      <button class="add-key-btn" @click="show_create_key()">
        <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
        Create API Key
      </button>
    </div>

    <!-- API Keys List -->
    <div v-else class="keys-list">
      <ContentBlock
        v-for="key in keys"
        :key="key.Id"
      >
        <div class="key-row">
          <div class="key-info">
            <div class="key-name">{{ key.Name }}</div>
            <div class="key-description">{{ key.Description }}</div>
          </div>
          <button class="delete-btn" @click="click_handler(key.Id)" title="Delete API key">
            <font-awesome-icon :icon="['fas', 'trash']" />
          </button>
        </div>
      </ContentBlock>

      <!-- Add API Key Button (below list when keys exist) -->
      <button class="add-key-btn" @click="show_create_key()">
        <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
        Create API Key
      </button>
    </div>
  </div>
</template>

<style scoped>
.apikeys-content {
  padding: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-faint);
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-muted);
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: var(--text-faint);
  margin: 0 0 1.5rem 0;
}

/* Keys List */
.keys-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

/* Each key sits in a <ContentBlock>; this row owns the inner flex layout. */
.key-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.key-info {
  flex: 1;
  min-width: 0;
}

.key-name {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.key-description {
  color: var(--text-muted);
  font-size: 0.875rem;
}

.delete-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  padding: 0.5rem 0.75rem;
  color: var(--text-faint);
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.delete-btn:hover {
  color: var(--color-error);
  border-color: rgba(239, 68, 68, 0.3);
  background: var(--color-error-soft);
}

/* Add Key Button */
.add-key-btn {
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.add-key-btn:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}

/* Prevent button from stretching when in keys-list */
.keys-list .add-key-btn {
  align-self: flex-start;
}
</style>
